import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { RunRecordTelemetrySeries } from '$features/report-hub/run-record/run-record-detail.types';

import styles from './run-record-runtime-chart.module.scss';

interface RunRecordRuntimeChartProps {
  series: RunRecordTelemetrySeries;
  /**
   * Total run duration in ms — used as the upper bound on the x-axis so a chart
   * with sparse samples still extends across the full runtime.
   */
  runtimeMs: number;
  /**
   * Hex color used for the line + gradient. Falls back to a neutral blue.
   */
  color?: string;
}

const AXIS_COLOR = '#9ca3af';
const TICK_COUNT = 6;

/**
 * Format milliseconds as "h:mm:ss" or "mm:ss" depending on whether the run
 * spans more than an hour. Used for the runtime x-axis ticks
 * ("x-axis starts at 0:00 and shows runtime, and not with datetime").
 */
function formatRuntime(runtimeMs: number, totalRuntimeMs: number): string {
  const totalSeconds = Math.max(0, Math.round(runtimeMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const showHours = totalRuntimeMs >= 60 * 60 * 1000;

  if (showHours) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0',
    )}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatValue(value: number | null, unit: string | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  const rounded = Math.round(value * 100) / 100;
  return unit ? `${rounded} ${unit}` : String(rounded);
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload?: { runtimeMs: number; value: number | null };
  }>;
  unit: string | null;
  runtimeMs: number;
}

function ChartTooltip({ active, payload, unit, runtimeMs }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipRuntime}>
        {formatRuntime(point.runtimeMs, runtimeMs)}
      </div>
      <div className={styles.tooltipValue}>{formatValue(point.value, unit)}</div>
    </div>
  );
}

export function RunRecordRuntimeChart({
  series,
  runtimeMs,
  color = '#3b82f6',
}: RunRecordRuntimeChartProps) {
  const chartData = useMemo(
    () =>
      series.samples
        .filter((sample) => sample.value !== null && Number.isFinite(sample.value))
        .map((sample) => ({
          runtimeMs: sample.runtimeMs,
          value: sample.value as number,
        })),
    [series.samples],
  );

  const ticks = useMemo(() => {
    if (runtimeMs <= 0) return [0];
    const step = runtimeMs / (TICK_COUNT - 1);
    return Array.from({ length: TICK_COUNT }, (_, i) => Math.round(i * step));
  }, [runtimeMs]);

  const gradientId = `runRecordChartGradient-${series.telemetryId}`;

  return (
    <div className={styles.chartContainer}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{series.name}</span>
        {series.unit && <span className={styles.headerUnit}>{series.unit}</span>}
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e5e7eb" vertical={false} />
          <XAxis
            type="number"
            dataKey="runtimeMs"
            domain={[0, runtimeMs]}
            ticks={ticks}
            tickFormatter={(value: number) => formatRuntime(value, runtimeMs)}
            stroke={AXIS_COLOR}
            fontSize={11}
          />
          <YAxis
            stroke={AXIS_COLOR}
            fontSize={11}
            width={48}
            tickFormatter={(value: number) => (series.unit ? `${value}` : String(value))}
          />
          <Tooltip content={<ChartTooltip unit={series.unit} runtimeMs={runtimeMs} />} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            connectNulls
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
